import * as Sentry from "@sentry/react";
import { getNetworkInfo, getNetworkSpeedCategory } from "./networkSpeed";

/**
 * Utility function to track performance and send events to Sentry
 * Similar to the loadTpViewer function pattern
 */
export const trackPerformance = async (operationName, operation, additionalData = {}) => {
  const start = performance.now(); // START TIMER
  
  // Get network info at the start of the operation
  let networkInfo = null;
  try {
    networkInfo = await getNetworkInfo();
  } catch (error) {
    console.warn('Failed to get network info:', error);
  }

  try {
    const result = await operation();
    return result;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  } finally {
    const end = performance.now();
    const duration = end - start; // Total time (ms)

    // Prepare network data for Sentry
    const networkData = networkInfo ? {
      network_connection_type: networkInfo.connection_type,
      network_downlink_mbps: networkInfo.downlink_mbps,
      network_rtt_ms: networkInfo.rtt_ms,
      network_save_data: networkInfo.save_data_mode,
      network_type: networkInfo.network_type,
      network_speed_category: getNetworkSpeedCategory(networkInfo),
      network_online_status: networkInfo.online_status,
      measured_speed_mbps: networkInfo.measured_speed_mbps
    } : {
      network_connection_type: 'unknown',
      network_online_status: navigator.onLine
    };

    // Send performance data to Sentry
    Sentry.captureEvent({
      message: operationName,
      level: "info",
      extra: {
        duration_ms: duration.toFixed(2),
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
        ...networkData,
        ...additionalData
      },
      tags: {
        performance_tracking: true,
        operation: operationName,
        network_type: networkData.network_connection_type,
        network_speed: networkData.network_speed_category
      }
    });

    console.log(`${operationName} Time:`, duration.toFixed(2), "ms", networkData.network_speed_category ? `(Network: ${networkData.network_speed_category})` : '');
  }
};

/**
 * Utility function to capture custom Sentry events with standard metadata
 */
export const captureCustomEvent = async (message, level = "info", extraData = {}, tags = {}) => {
  // Get network info for context
  let networkInfo = null;
  try {
    networkInfo = await getNetworkInfo();
  } catch (error) {
    console.warn('Failed to get network info for custom event:', error);
  }

  const networkData = networkInfo ? {
    network_connection_type: networkInfo.connection_type,
    network_downlink_mbps: networkInfo.downlink_mbps,
    network_rtt_ms: networkInfo.rtt_ms,
    network_speed_category: getNetworkSpeedCategory(networkInfo),
    network_online_status: networkInfo.online_status
  } : {
    network_connection_type: 'unknown',
    network_online_status: navigator.onLine
  };

  Sentry.captureEvent({
    message,
    level,
    extra: {
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      timestamp: new Date().toISOString(),
      ...networkData,
      ...extraData
    },
    tags: {
      custom_event: true,
      network_type: networkData.network_connection_type,
      network_speed: networkData.network_speed_category,
      ...tags
    }
  });
};

/**
 * Example usage for the loadTpViewer function
 */
export const loadTpViewer = async () => {
  return trackPerformance("tp_viewer_load_time", async () => {
    // your multiple API calls
    await fetch("/api/1");
    await fetch("/api/2");
    await fetch("/api/3");
    await fetch("/api/4");
  });
};
